import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useMemo } from 'react';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import {
  filterEigenLinks,
  useHoofdstukHB,
  useLinksInHoofdstuk,
} from '@/api/queries/objects';
import { fetchAllLinksData } from '@/api/queries/objects/utils/fetchAllLinksData';
import { useOpenModal } from '@/components/Modal';
import { RegularList } from '@/icons/components';
import { JumpToParagraaf } from '@/modals/JumpToParagraaf';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';

import { BesluitChapterNavigation } from '../-components/BesluitChapterNavigation';
import { Body } from '../-components/Body';
import { ViewerHoofdstuk } from '../-components/ViewerHoofdstuk';
import { ViewerParagraaf } from '../-components/ViewerParagraaf';
import { ViewerSubparagraaf } from '../-components/ViewerSubparagraaf';
import { ViewerVoorschrift } from '../-components/ViewerVoorschrift';
import { getObjectStatus } from '../-components/utils/getObjectState';
import {
  ensureQueryDataViewerBesluitHoofdstukken,
  useViewerBesluitHoofdstukken,
} from './-utils/useViewerBesluitHoofdstukken';

const linksDataQueryOptions = (hoofdstukId: string, besluitId: string) => {
  return queryOptions({
    queryKey: ['viewer-besluit-links-data', { hoofdstukId, besluitId }],
    queryFn: async () => {
      const links = await useLinksInHoofdstuk.fetchQuery(
        {
          hoofdstukId,
          besluitId,
          VSPBFilter: [
            { model: 'VSPB', field: 'Type', op: 'eq', value: 'Eigen' },
          ],
        },
        { staleTime: Infinity }
      );

      const eigenLinks = filterEigenLinks(links ?? []);

      return await fetchAllLinksData(eigenLinks);
    },
  });
};

/**
 * Test ID utils
 */
const paragraafTestId = (paragraafId: string) => `paragraaf-${paragraafId}`;
const subparagraafTestId = (subparagraafId: string) =>
  `subparagraaf-${subparagraafId}`;
const voorschriftTestId = (voorschriftId: string) =>
  `voorschrift-${voorschriftId}`;

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout/voorschriften/$hoofdstukId'
)({
  loader: async ({
    params: { besluitId, bedrijfLineageId, hoofdstukId },
    context: { queryClient },
  }) => {
    try {
      const [grondslagen] = await Promise.all([
        ensureQueryDataViewerBesluitHoofdstukken({ besluitId, queryClient }),
        queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
        queryClient.ensureQueryData(
          useLatestBedrijf.getOptions(bedrijfLineageId)
        ),
        queryClient.ensureQueryData(
          linksDataQueryOptions(hoofdstukId, besluitId)
        ),
        queryClient.ensureQueryData(
          useHoofdstukHB.getOptions({ besluitId, hoofdstukId })
        ),
      ]);

      if (!grondslagen.length) throw notFound();
    } catch {
      throw notFound();
    }
  },
  component: VoorschriftenComponent,
});

/**
 * Route component
 */
function VoorschriftenComponent() {
  const { besluitId, hoofdstukId, bedrijfLineageId } = Route.useParams();
  const openModal = useOpenModal();

  /**
   * Fetch data
   */
  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluit = useBesluitId(besluitId, { staleTime: Infinity });
  const { grondslagen } = useViewerBesluitHoofdstukken({ besluitId });
  const linksData = useQuery(linksDataQueryOptions(hoofdstukId, besluitId));

  const HB = useHoofdstukHB(
    { besluitId, hoofdstukId },
    { staleTime: Infinity }
  );

  /**
   * Virtualizer
   */
  const { ref: listRef, offsetTop } = useOffsetTop();

  const virtualizer = useWindowVirtualizer({
    count: linksData.data?.length ?? 0,
    estimateSize: () => 1000,
    overscan: 2,
    enabled: !!linksData.data?.length,
    paddingEnd: 200,
    initialOffset: 0, // Fixes an issue where the page starts at the middle of the container
    scrollMargin: offsetTop,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const hoofdstuk = useMemo(() => {
    const hoofdstuk = grondslagen
      .flatMap((grondslag) => grondslag.hoofdstukken)
      .find((hoofdstuk) => hoofdstuk.data.ID === hoofdstukId);

    return hoofdstuk;
  }, [grondslagen, hoofdstukId]);

  /**
   * Catch loading states
   */
  if (
    !besluit.data ||
    !bedrijf.data ||
    !HB.data ||
    linksData.isLoading ||
    !hoofdstuk
  )
    return null;

  return (
    <>
      <Body.ContentHeader
        sticky
        metadataSwitch
        button={{
          onClick: () => {
            openModal(JumpToParagraaf, {
              onClick: ({ index }) => {
                virtualizer.scrollToIndex(index, { align: 'start' });
              },
              hoofdstukNummer: hoofdstuk.data.Nummer,
              paragrafen:
                linksData.data?.map(({ data, PHB }) => {
                  const state = getObjectStatus(data, PHB.Type === 'Eigen');

                  return {
                    data,
                    state: state
                      ? {
                          state,
                          besluitTypeId: besluit.data.Type,
                        }
                      : undefined,
                  };
                }) ?? [],
            });
          },
          icon: RegularList,
          children: 'Alle paragrafen',
        }}
      >
        <Body.BesluitTitle besluit={besluit.data} />
      </Body.ContentHeader>

      <Body.BesluitDescriptionList besluit={besluit.data} />

      {totalSize === 0 ? (
        <ViewerHoofdstuk
          className="mt-2"
          hoofdstuk={hoofdstuk.data}
          besluitTypeId={besluit.data.Type}
          HB={HB.data}
          mode="besluit"
        />
      ) : (
        <div
          className="relative w-full"
          style={{
            height: totalSize,
          }}
          ref={listRef}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${(virtualItems[0]?.start ?? 0) - offsetTop}px)`,
            }}
            className="space-y-2 pt-2"
          >
            <ViewerHoofdstuk
              hoofdstuk={hoofdstuk.data}
              besluitTypeId={besluit.data.Type}
              HB={HB.data}
              mode="besluit"
            />

            {virtualItems.map((item) => {
            const paragraaf = linksData.data?.[item.index] ?? '';

            if (!paragraaf) return null;

            return (
              <ViewerParagraaf
                key={item.key}
                ref={virtualizer.measureElement}
                data-index={item.index}
                besluitTypeId={besluit.data.Type}
                hoofdstukNummer={hoofdstuk.data.Nummer}
                paragraaf={paragraaf.data}
                PHB={paragraaf.PHB}
                mode="besluit"
                testId={paragraafTestId(paragraaf.data.ID)}
              >
                <div className="space-y-2 py-2">
                  {paragraaf.subparagrafen.map((subparagraaf) => {
                    return (
                      <ViewerSubparagraaf
                        besluitTypeId={besluit.data.Type}
                        subparagraaf={subparagraaf.data}
                        SPPB={subparagraaf.SPPB}
                        hasVoorschriften={subparagraaf.voorschriften.length > 0}
                        mode="besluit"
                        testId={subparagraafTestId(subparagraaf.data.ID)}
                        key={`viewer-besluit-subparagraaf-${subparagraaf.data.ID}`}
                      >
                        <div className="space-y-2 py-2">
                          {subparagraaf.voorschriften.map((voorschrift) => {
                            const state = getObjectStatus(
                              voorschrift.data,
                              true
                            );

                            return (
                              <ViewerVoorschrift
                                key={`viewer-besluit-voorschrift-${voorschrift.data.ID}`}
                                hoofdstuk={hoofdstuk.data}
                                testId={voorschriftTestId(
                                  voorschrift.VSPB.Voorschrift
                                )}
                                state={
                                  state
                                    ? {
                                        besluitTypeId: besluit.data.Type,
                                        state,
                                      }
                                    : undefined
                                }
                                VSPB={voorschrift.VSPB}
                                paragraafId={paragraaf.data.ID}
                                paragraafNummer={paragraaf.data.Nummer}
                                voorschrift={voorschrift.data}
                                mode="besluit"
                              />
                            );
                          })}
                        </div>
                      </ViewerSubparagraaf>
                    );
                  })}
                </div>
              </ViewerParagraaf>
            );
            })}
          </div>
        </div>
      )}

      <BesluitChapterNavigation
        groupedGrondslag={grondslagen}
        hoofdstukId={hoofdstukId}
      />
    </>
  );
}
